import styled from "styled-components";
import Link from "next/link";
import { Button, Grid } from "antd";
import { noop } from "lodash";

import { RATypography } from "../Typography";
import { LoadingOutlined } from "@ant-design/icons";
import { lightTheme } from "@/themes/ThemeConfig";
import { SvgBackToTop } from "@/assets/icons";

const { useBreakpoint } = Grid;

export const BUTTON_SIZES = {
	large: "large",
	medium: "medium",
	small: "small",
};

export const RALink = ({ title = "", href = "/", variant = "body3", color = "#595959" }) => {
	return (
		<Link href={href}>
			<RATypography variant={variant} color={color}>
				{title}
			</RATypography>
		</Link>
	);
};

export const RAButtonPrimary = ({
	title = "",
	type = "button",
	href = "/",
	width,
	onClick = noop,
	loading,
	disabled,
	size,
	background,
	icon,
	textColor = null,
	target = "",
	"data-testid": dataTestId = null,
	...props
}) => {
	const screens = useBreakpoint();

	if (type === "link") {
		return (
			<Link href={href} target={target} prefetch={false} style={{ display: "inline-flex" }}>
				<WrapperPrimaryBtn
					disabled={disabled}
					width={width}
					height={size ? size : screens?.lg ? BUTTON_SIZES.large : BUTTON_SIZES.medium}
					style={background ? { background } : {}}
					data-testid={dataTestId}
					{...props}
				>
					{!loading ? (
						<RATypography
							variant={
								size === BUTTON_SIZES.small
									? "button3"
									: size === BUTTON_SIZES.medium
										? "button2"
										: "button1"
							}
							color={textColor ?? (disabled ? lightTheme.color_text_200 : lightTheme.color_text_500)}
							maxLines={3}
						>
							{title}
						</RATypography>
					) : (
						<LoadingOutlined style={{ fontSize: "24px", color: "#FFB11A" }} />
					)}
				</WrapperPrimaryBtn>
			</Link>
		);
	}

	if (type === "submit") {
		return (
			<WrapperPrimaryBtn
				style={{ background: background ?? "transparent" }}
				width={width}
				disabled={disabled}
				onClick={onClick}
				height={size ? size : screens?.lg ? BUTTON_SIZES.large : BUTTON_SIZES.medium}
				data-testid={dataTestId}
				{...props}
			>
				{icon}
				{!loading ? (
					<RATypography
						variant={
							size === BUTTON_SIZES.small
								? "button3"
								: size === BUTTON_SIZES.medium
									? "button2"
									: "button1"
						}
						classname="ra-primary-button-text"
						color={textColor ?? (disabled ? lightTheme.color_text_200 : lightTheme.color_text_500)}
						maxLines={3}
					>
						{title}
					</RATypography>
				) : (
					<LoadingOutlined style={{ fontSize: "24px", color: "#FFB11A" }} />
				)}
			</WrapperPrimaryBtn>
		);
	}

	return (
		<WrapperPrimaryBtn
			style={{ background: background ?? "transparent" }}
			width={width}
			disabled={disabled}
			onClick={onClick}
			height={size ? size : screens?.lg ? BUTTON_SIZES.large : BUTTON_SIZES.medium}
			data-testid={dataTestId}
			{...props}
		>
			{icon}
			{!loading ? (
				<RATypography
					variant={
						size === BUTTON_SIZES.small ? "button3" : size === BUTTON_SIZES.medium ? "button2" : "button1"
					}
					classname="ra-primary-button-text"
					color={textColor ?? (disabled ? lightTheme.color_text_200 : lightTheme.color_text_500)}
					maxLines={3}
				>
					{title}
				</RATypography>
			) : (
				<LoadingOutlined style={{ fontSize: "24px", color: "#FFB11A" }} />
			)}
		</WrapperPrimaryBtn>
	);
};

export const RAButtonSecondary = ({
	title = "",
	type = "button",
	width,
	onClick = noop,
	background,
	icon,
	disabled,
	size,
	className,
	border,
	"data-testid": dataTestId = null,
}) => {
	const screens = useBreakpoint();
	if (type === "submit") {
		return (
			<WrapperButtonSubmit
				disabled={disabled}
				onClick={onClick}
				width={width}
				height={size ? size : screens?.lg ? BUTTON_SIZES.large : BUTTON_SIZES.medium}
			>
				<RATypography
					variant={
						size === BUTTON_SIZES.small ? "button3" : size === BUTTON_SIZES.medium ? "button2" : "button1"
					}
					color={!disabled ? lightTheme.color_text_500 : lightTheme.color_text_200}
					maxLines={3}
				>
					{title}
				</RATypography>
			</WrapperButtonSubmit>
		);
	}

	return (
		<WrapperSecondaryBtn
			className={className}
			background={background}
			width={width}
			onClick={() => !disabled && onClick()}
			disabled={disabled}
			border={border}
			height={size ? size : screens?.lg ? BUTTON_SIZES.large : BUTTON_SIZES.medium}
			data-testid={dataTestId}
		>
			{icon}
			<RATypography
				variant={size === BUTTON_SIZES.small ? "button3" : size === BUTTON_SIZES.medium ? "button2" : "button1"}
				color={!disabled ? lightTheme.color_text_500 : lightTheme.color_text_200}
				maxLines={3}
			>
				{title}
			</RATypography>
		</WrapperSecondaryBtn>
	);
};

export const RAButtonGhost = ({
	title = "",
	width,
	onClick = noop,
	prefix,
	suffix,
	"data-testid": dataTestId = null,
}) => {
	return (
		<WrapperGhostBtn width={width} onClick={onClick} data-testid={dataTestId}>
			{prefix}
			<TextGhost>{title}</TextGhost>
			{suffix}
		</WrapperGhostBtn>
	);
};

export const RAButtonBackToTop = (props) => {
	return (
		<div
			style={{
				position: "absolute",
				right: 20,
				bottom: 20,
			}}
		>
			<RAButtonPrimary icon={<SvgBackToTop />} title="BACK TO TOP" {...props} />
		</div>
	);
};

const WrapperPrimaryBtn = styled(Button)`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	padding: 17px 28px;
	gap: 8px;
	width: ${(props) => props.width || "auto"};
	height: ${(props) =>
		props.height === BUTTON_SIZES.small ? "40px" : props.height === BUTTON_SIZES.medium ? "44px" : "52px"};
	border: 1px solid #ffb11a;
	border-radius: 50px;
	cursor: pointer;

	${({ disabled }) =>
		disabled &&
		`
        cursor: not-allowed;
        border: 1px solid #E9E8E8;
    `}
	@media (max-width: 576px) {
		padding: 17px 16px;
	}
`;
const WrapperSecondaryBtn = styled(Button)`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	padding: 17px 28px;
	gap: 8px;
	height: ${(props) =>
		props.height === BUTTON_SIZES.small ? "40px" : props.height === BUTTON_SIZES.medium ? "44px" : "52px"};
	border: ${(props) => props.border || "1px solid #d6d6d6"};
	border-radius: 50px;
	background-color: ${(props) => props.background || "tranparent"};
	cursor: pointer;
	letter-spacing: 3px;
	text-indent: 3px;
	width: ${(props) => props.width || "auto"};

	${({ disabled }) =>
		disabled &&
		`
        cursor: not-allowed;
    `}
	@media (max-width: 576px) {
		padding: 17px 16px;
	}
`;
const WrapperGhostBtn = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	padding: 10px 20px;
	gap: 8px;
	width: ${(props) => props.width};
	cursor: pointer;
`;
const WrapperButtonSubmit = styled(Button)`
	padding: 0 20px;
	height: ${(props) =>
		props.height === BUTTON_SIZES.small ? "40px" : props.height === BUTTON_SIZES.medium ? "44px" : "52px"};
	${({ disabled }) =>
		disabled &&
		`
        cursor: not-allowed;
    `}
`;
const TextGhost = styled.div`
	font-family: "DM Sans";
	font-style: normal;
	font-weight: 500;
	font-size: 10px;
	line-height: 30px;
	display: flex;
	align-items: center;
	letter-spacing: 2px;
	text-indent: 2px;
	text-transform: uppercase;
	color: ${(props) => props.theme.color_text_500};
`;
